<template>
  <div class="pt-2 pb-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <section class="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <default-card-component :quantity="stats.SalesPVPCurrentMonth + '€'" :loading="stats.loading" icon="shopping-cart" :variation="salesPvpVariation">
          {{ $t("sales.pvp_sales") }} {{ periodLabel }}
        </default-card-component>

        <default-card-component :quantity="stats.SalesNetCurrentMonth + '€'" :loading="stats.loading" icon="wallet" :variation="salesNetVariation">
          {{ $t("sales.net_sales") }} {{ periodLabel }}
        </default-card-component>

        <default-card-component :quantity="stats.RefundsPVPCurrentMonth + '€'" text-color="text-red-600" :loading="stats.loading" icon="ban" :variation="refundsPvpVariation">
          {{ $t("sales.pvp_returns") }} {{ periodLabel }}
        </default-card-component>

        <default-card-component :quantity="stats.RefundsNetCurrentMonth + '€'" text-color="text-red-600" :loading="stats.loading" icon="ban" :variation="refundsNetVariation">
          {{ $t("sales.net_returns") }} {{ periodLabel }}
        </default-card-component>
      </section>

      <section class="my-4 bg-white overflow-hidden shadow sm:rounded-lg">
        <div class="px-4 py-5 sm:p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label for="years" class="block text-sm leading-5 font-medium text-gray-700">{{$t('general.year')}}</label>
            <select id="years" v-model="request.year" v-on:change="getOrders" :disabled="yearSelectDisabled" class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5 disabled:bg-gray-100 disabled:text-gray-400">
              <option v-for="i in range(2015, currentYear() - 1)" :key="i" :value="i">{{i}}</option>
              <option selected :key="currentYear()" :value="currentYear()">{{currentYear()}}</option>
            </select>
          </div>
          <div>
            <label for="period" class="block text-sm leading-5 font-medium text-gray-700">{{$t('sales.period')}}</label>
            <select id="period" v-model="request.period" v-on:change="getOrders" class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5">
              <option value="q1">{{$t('sales.q1')}}</option>
              <option value="q2">{{$t('sales.q2')}}</option>
              <option value="q3">{{$t('sales.q3')}}</option>
              <option value="q4">{{$t('sales.q4')}}</option>
              <option value="full_year">{{$t('sales.full_year')}}</option>
              <option value="last_12_months">{{$t('sales.last_12_months')}}</option>
              <option value="previous_year">{{$t('sales.previous_year')}}</option>
            </select>
          </div>
          <div class="flex items-end">
            <button type="button" v-on:click="exportOrders" class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
              <font-awesome-icon icon="file-excel" class="h-4 w-4 mr-2" />
              {{$t('sales.export_excel')}}
            </button>
          </div>
          <div v-if="orders" class="col-span-2 lg:col-span-4 mt-1">
            <p class="text-bppick">{{$t('sales.obtain_text_1')}} {{orders.length}} {{$t('sales.obtain_text_2')}}</p>
          </div>
        </div>
      </section>

      <!-- Replace with your content -->
      <div class="py-4 hidden md:block">
        <div class="flex flex-col">
          <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div v-if="!loading" class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
              <table class="min-w-full table-fixed">
                <thead>
                <tr>
                  <th class="w-28 px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th class="w-36 px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t("general.date") }}
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t("sales.customer") }}
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t("general.products") }}
                  </th>
                  <th class="w-20 px-3 py-3 border-b border-gray-200 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t("general.pvp") }}
                  </th>
                  <th class="w-20 px-3 py-3 border-b border-gray-200 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t("general.net") }}
                  </th>
                  <th class="w-20 px-3 py-3 border-b border-gray-200 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {{ $t("sales.bank_fee") }}
                  </th>
                  <th class="w-28 px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t("general.state") }}
                  </th>
                  <th class="w-10 px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"></th>
                  <th class="hidden px-3 py-3 border-b border-gray-200 bg-gray-50"></th>
                </tr>
                </thead>
                <tbody class="bg-white">
                <tr v-for="order in orders" :key="order.uuid" v-show="order.customer.firstname !== null">
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 align-middle">
                    <div class="flex items-center gap-1">
                      <span class="text-sm leading-5 font-medium font-mono text-gray-900" :title="order.uuid">{{ order.uuid.substring(0, 8) }}…</span>
                      <button type="button" class="text-gray-400 hover:text-gray-600 focus:outline-none" @click="copyOrderId(order)" :title="$t('sales.copy_id')" :aria-label="$t('sales.copy_id')">
                        <font-awesome-icon :icon="copiedUuid === order.uuid ? 'check' : 'copy'" :class="copiedUuid === order.uuid ? 'text-green-600' : ''" class="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 align-middle">
                    <div class="text-sm leading-5 font-medium text-gray-900">{{ $moment(order.completed_at).format('DD/MM/YYYY H:mm:ss') }}</div>
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 align-middle">
                    <router-link v-if="order.customer && order.customer.uuid" :to="{ name: 'user-profile', params: { id: order.customer.uuid }}" class="cursor-pointer hover:text-blue-800 hover:underline">
                      <div class="text-sm leading-5 font-medium text-bpblue">
                        {{ order.customer.firstname + ' ' + order.customer.lastname }}
                      </div>
                      <div v-if="order.customer.email" class="text-sm leading-3 font-light text-bpblue break-words">
                        {{ order.customer.email }}
                      </div>
                    </router-link>
                    <template v-else>
                      <div v-if="order.customer" class="text-sm leading-5 font-medium text-gray-900">
                        {{ order.customer.firstname + ' ' + order.customer.lastname }}
                      </div>
                      <div v-if="order.customer.email" class="text-sm leading-3 font-light text-gray-900 break-words">
                        {{ order.customer.email }}
                      </div>
                    </template>
                    <div v-if="order.customer.phone" class="text-sm leading-3 font-light text-gray-900">
                      {{ order.customer.phone }}
                    </div>
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500 align-middle">
                    <div v-for="item in order.items" :key="item.uuid" class="mb-1 last:mb-0">
                      <div>{{ parseItemTitle(item.title).name }}</div>
                      <span v-for="(coupon, index) in parseItemTitle(item.title).coupons" :key="index" class="inline-flex items-center gap-1 mr-1 mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                        <font-awesome-icon icon="ticket-alt" class="h-3 w-3" />
                        {{ coupon.code }} -{{ coupon.amount }}{{ coupon.symbol }}
                      </span>
                    </div>
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500 text-right align-middle">
                    {{ order.total }}€
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500 text-right align-middle">
                    {{ order.net }}€
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500 text-right align-middle">
                    {{ order.metadata._stripe_fee }}€
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500 align-middle">
                    <span class="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full" :class="orderStatusBadge(order.status).class">{{ orderStatusBadge(order.status).label }}</span>
                  </td>
                  <td class="px-3 py-4 whitespace-no-wrap text-right border-b border-gray-200 text-sm leading-5 font-medium align-middle">
                    <a href="#" class="hidden text-blue-600 hover:text-blue-900 hover:underline">{{ $t("sales.view_order") }}</a>
                  </td>
                  <td class="hidden lg:px-6 py-4 text-right border-b border-gray-200 text-sm leading-5 font-medium align-middle">
                    <router-link :to="{ name: 'reports.orders.show_order', params: { id : order.uuid }}" class="cursor-pointer hover:underline">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-3 h-5 w-5 text-bpblue hover:text-blue-300 transition rounded ease-in-out duration-150">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </router-link>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <loading-component v-else>{{ $t("sales.loading_orders") }}</loading-component>
          </div>
        </div>
      </div>
      <mobile-orders-component class="md:hidden" :loading="loading" :orders="orders"></mobile-orders-component>
      <!--<PaginationComponent></PaginationComponent>-->
      <!-- /End replace -->
    </div>
  </div>
</template>

<script>
import LoadingComponent from "@/views/common/LoadingComponent";
import OrdersService from "@/services/report/OrdersService";
import DefaultCardComponent from "@/components/cards/DefaultCardComponent";
import MobileOrdersComponent from "@/views/sales/Mobile/OrdersComponent";
import NotificationService from "@/services/notification/NotificationService";
import {useAuthUserStore} from "@/stores/auth";
import {useAcademiesStore} from "@/stores/academies";

export default {
  name: "OrdersComponent",
  components: {DefaultCardComponent, LoadingComponent, MobileOrdersComponent},
  data() {
    return {
      loading: true,
      orders: [],
      request: {
        year: null,
        period: 'full_year'
      },
      stats: {
        loading: true,
        SalesPVPCurrentMonth: 0,
        SalesNetCurrentMonth: 0,
        RefundsPVPCurrentMonth: 0,
        RefundsNetCurrentMonth: 0,
        comparisonLoading: false,
        previous: {
          SalesPVPCurrentMonth: null,
          SalesNetCurrentMonth: null,
          RefundsPVPCurrentMonth: null,
          RefundsNetCurrentMonth: null
        }
      },
      copiedUuid: null,
      requestId: 0
    }
  },
  setup () {

    const authUserStore = useAuthUserStore()
    const academiesStore = useAcademiesStore()

    return {
      authUserStore,
      academiesStore
    }
  },
  created() {
    this.request.year = this.$moment().year()

    if (this.academiesStore.academy.uuid) {
      this.getOrders()
    } else {
      const unwatch = this.$watch(
        () => this.academiesStore.academy.uuid,
        (uuid) => {
          if (uuid) {
            unwatch()
            this.getOrders()
          }
        }
      )
    }
  },
  computed: {
    yearSelectDisabled() {
      return this.request.period === 'last_12_months' || this.request.period === 'previous_year'
    },
    periodLabel() {
      switch (this.request.period) {
        case 'q1': return `${this.$t('sales.q1')} ${this.request.year}`
        case 'q2': return `${this.$t('sales.q2')} ${this.request.year}`
        case 'q3': return `${this.$t('sales.q3')} ${this.request.year}`
        case 'q4': return `${this.$t('sales.q4')} ${this.request.year}`
        case 'full_year': return `${this.request.year}`
        case 'last_12_months': return this.$t('sales.last_12_months')
        case 'previous_year': return `${this.$moment().year() - 1}`
        default: return ''
      }
    },
    salesPvpVariation() {
      return this.computeVariation(this.stats.SalesPVPCurrentMonth, this.stats.previous.SalesPVPCurrentMonth, true)
    },
    salesNetVariation() {
      return this.computeVariation(this.stats.SalesNetCurrentMonth, this.stats.previous.SalesNetCurrentMonth, true)
    },
    refundsPvpVariation() {
      return this.computeVariation(this.stats.RefundsPVPCurrentMonth, this.stats.previous.RefundsPVPCurrentMonth, false)
    },
    refundsNetVariation() {
      return this.computeVariation(this.stats.RefundsNetCurrentMonth, this.stats.previous.RefundsNetCurrentMonth, false)
    }
  },
  methods: {
    getDateRange() {
      const year = parseInt(this.request.year)
      let from
      let to

      switch (this.request.period) {
        case 'q1':
        case 'q2':
        case 'q3':
        case 'q4': {
          const quarter = parseInt(this.request.period.replace('q', ''))
          const base = this.$moment(`${year}-01-01`).quarter(quarter)
          from = base.clone().startOf('quarter')
          to = base.clone().endOf('quarter')
          break
        }
        case 'last_12_months':
          to = this.$moment().endOf('day')
          from = this.$moment().subtract(12, 'months').startOf('day')
          break
        case 'previous_year': {
          const previousYear = this.$moment().year() - 1
          from = this.$moment(`${previousYear}-01-01`).startOf('year')
          to = this.$moment(`${previousYear}-01-01`).endOf('year')
          break
        }
        case 'full_year':
        default:
          from = this.$moment(`${year}-01-01`).startOf('year')
          to = this.$moment(`${year}-01-01`).endOf('year')
      }

      const now = this.$moment().endOf('day')
      if (to.isAfter(now)) {
        to = now
      }

      return { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }
    },
    getPreviousDateRange() {
      const year = parseInt(this.request.year)
      let from
      let to

      switch (this.request.period) {
        case 'q1':
        case 'q2':
        case 'q3':
        case 'q4': {
          const quarter = parseInt(this.request.period.replace('q', ''))
          let previousQuarter = quarter - 1
          let previousYear = year
          if (previousQuarter < 1) {
            previousQuarter = 4
            previousYear = year - 1
          }
          const base = this.$moment(`${previousYear}-01-01`).quarter(previousQuarter)
          from = base.clone().startOf('quarter')
          to = base.clone().endOf('quarter')
          break
        }
        case 'last_12_months':
          to = this.$moment().subtract(12, 'months').subtract(1, 'day').endOf('day')
          from = this.$moment().subtract(24, 'months').startOf('day')
          break
        case 'previous_year': {
          const yearBeforeThat = this.$moment().year() - 2
          from = this.$moment(`${yearBeforeThat}-01-01`).startOf('year')
          to = this.$moment(`${yearBeforeThat}-01-01`).endOf('year')
          break
        }
        case 'full_year':
        default:
          from = this.$moment(`${year - 1}-01-01`).startOf('year')
          to = this.$moment(`${year - 1}-01-01`).endOf('year')
      }

      // Si el periodo actual seleccionado todavía no ha terminado (p. ej. el año en curso),
      // recorta el periodo anterior a la misma cantidad de días transcurridos para que la
      // comparación sea entre periodos equivalentes y no entre uno parcial y otro completo.
      if (this.request.period === 'full_year' || ['q1', 'q2', 'q3', 'q4'].includes(this.request.period)) {
        const currentRange = this.getDateRange()
        const elapsedDays = this.$moment(currentRange.to).diff(this.$moment(currentRange.from), 'days')
        const clippedTo = from.clone().add(elapsedDays, 'days').endOf('day')
        if (clippedTo.isBefore(to)) {
          to = clippedTo
        }
      }

      return { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }
    },
    buildRequestParams(dateRange) {
      return {
        academy_uuid: this.academiesStore.academy.uuid,
        from: dateRange.from,
        to: dateRange.to
      }
    },
    computeVariation(current, previousValue, higherIsGood) {
      if (this.stats.comparisonLoading) return null
      const previousNum = parseFloat(previousValue)
      if (previousValue === null || isNaN(previousNum) || previousNum === 0) return null

      const currentNum = parseFloat(current)
      const percent = ((currentNum - previousNum) / previousNum) * 100
      const isIncrease = percent >= 0
      const isGood = higherIsGood ? isIncrease : !isIncrease

      return { percent, isGood }
    },
    range (start, end, step = 1) {
      const len = Math.floor((end - start) / step) + 1
      return Array(len).fill().map((_, idx) => start + (idx * step))
    },
    currentYear() {
      return parseInt(this.$moment().format('YYYY'));
    },
    parseItemTitle(title) {
      const couponSuffix = /\s-\s\(#([^/]+?)\s\/\s([\d.,]+)\s*(%|EUR)?\)/g

      const coupons = []
      let match
      while ((match = couponSuffix.exec(title)) !== null) {
        coupons.push({ code: match[1].trim(), amount: match[2].trim(), symbol: match[3] || '' })
      }

      const name = title.replace(/\s-\s\(#([^/]+?)\s\/\s([\d.,]+)\s*(%|EUR)?\)/g, '').trim()

      return { name, coupons }
    },
    orderStatusBadge(status) {
      const statuses = {
        completed:  { label: this.$t('sales.complete'),   class: 'bg-green-100 text-green-800' },
        processing: { label: this.$t('sales.processing'), class: 'bg-blue-100 text-blue-800' },
        pending:    { label: this.$t('sales.pending'),     class: 'bg-yellow-100 text-yellow-800' },
        on_hold:    { label: this.$t('sales.on_hold'),     class: 'bg-orange-100 text-orange-800' },
        refunded:   { label: this.$t('sales.reimbursed'), class: 'bg-purple-100 text-purple-800' },
        failed:     { label: this.$t('sales.failed'),      class: 'bg-red-100 text-red-800' },
        cancelled:  { label: this.$t('sales.cancelled'),   class: 'bg-gray-100 text-gray-800' },
        trash:      { label: this.$t('sales.trashed'),     class: 'bg-gray-300 text-gray-700' },
      }

      return statuses[status] || { label: status, class: 'bg-gray-100 text-gray-800' }
    },
    copyOrderId(order) {
      navigator.clipboard.writeText(order.uuid).then(() => {
        this.copiedUuid = order.uuid
        setTimeout(() => {
          if (this.copiedUuid === order.uuid) this.copiedUuid = null
        }, 1500)
      }).catch((error) => {
        console.log("No se pudo copiar el ID del pedido", error)
      })
    },
    getOrders() {
      const requestId = ++this.requestId
      this.loading = true;

      const params = this.buildRequestParams(this.getDateRange())

      OrdersService.index(params).then((response) => {
        if (requestId !== this.requestId) return
        console.log("getOrders OK", response)
        this.orders = response.data.orders
        this.getStatsForPeriod(requestId)
      })
      .catch((response) => {
        if (requestId !== this.requestId) return
        console.log("Ha ocurrido un error en getOrders", response)
        this.stats.loading = false
      })
      .finally(() => {
        if (requestId === this.requestId) this.loading = false;
      })
    },
    exportOrders() {
      const params = this.buildRequestParams(this.getDateRange())

      OrdersService.download(params)
          .then((response) => {
            console.log("Descargando pedidos", response)
            let url = window.URL.createObjectURL(new Blob([response.data]));
            let link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'pedidos.xlsx');
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          })
          .catch((error) => {
            console.log("Error en la descarga de pedidos", error.response)
            if (error.response.data.errors) {
              error.response.data.errors.forEach((error) => {
                NotificationService.showNotification(error, "error")
              })
            } else {
              NotificationService.showNotification("Ha ocurrido un error inesperado", "error")
            }
          })
    },
    computeStatsFromOrders(orders) {
      let salesPvp = 0
      let salesNet = 0
      let refundsPvp = 0
      let refundsNet = 0

      orders.forEach( (order) => {
        if (order.status == 'completed')
        {
          salesPvp += order.total * 100;
          salesNet += order.net * 100;
        }

        if (order.status == 'refunded')
        {
          refundsPvp += order.total * 100;
          refundsNet += order.net * 100;
        }
      })

      return {
        SalesPVPCurrentMonth: (salesPvp / 100).toFixed(2),
        SalesNetCurrentMonth: (salesNet / 100).toFixed(2),
        RefundsPVPCurrentMonth: (refundsPvp / 100).toFixed(2),
        RefundsNetCurrentMonth: (refundsNet / 100).toFixed(2)
      }
    },
    getStatsForPeriod(requestId) {
      this.stats.loading = true

      const result = this.computeStatsFromOrders(this.orders)
      this.stats.SalesPVPCurrentMonth = result.SalesPVPCurrentMonth
      this.stats.SalesNetCurrentMonth = result.SalesNetCurrentMonth
      this.stats.RefundsPVPCurrentMonth = result.RefundsPVPCurrentMonth
      this.stats.RefundsNetCurrentMonth = result.RefundsNetCurrentMonth

      this.stats.loading = false

      this.getPreviousPeriodStats(requestId)
    },
    getPreviousPeriodStats(requestId) {
      this.stats.comparisonLoading = true
      this.stats.previous.SalesPVPCurrentMonth = null
      this.stats.previous.SalesNetCurrentMonth = null
      this.stats.previous.RefundsPVPCurrentMonth = null
      this.stats.previous.RefundsNetCurrentMonth = null

      const params = this.buildRequestParams(this.getPreviousDateRange())

      OrdersService.index(params).then((response) => {
        if (requestId !== this.requestId) return
        const result = this.computeStatsFromOrders(response.data.orders)
        this.stats.previous.SalesPVPCurrentMonth = result.SalesPVPCurrentMonth
        this.stats.previous.SalesNetCurrentMonth = result.SalesNetCurrentMonth
        this.stats.previous.RefundsPVPCurrentMonth = result.RefundsPVPCurrentMonth
        this.stats.previous.RefundsNetCurrentMonth = result.RefundsNetCurrentMonth
      })
      .catch((response) => {
        if (requestId !== this.requestId) return
        console.log("Ha ocurrido un error en getPreviousPeriodStats", response)
      })
      .finally(() => {
        if (requestId === this.requestId) this.stats.comparisonLoading = false
      })
    }
  }
}
</script>

<style scoped>

</style>